import { useCallback, useMemo } from 'react'
import { useQuery, type QueryClient } from '@tanstack/react-query'
import { getMultiCartItems } from 'api/cart/getMultiCartItems'
import * as CartKeys from 'tripPlanner/hooks/reactQueryKeys/cart'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import {
  isMissingTripPlannerCartError,
  mapTripPlannerCartItemsToMap,
  type TripPlannerCartItemsData,
} from '../utils/tripPlannerCartItemsUtils'

export const tripPlannerCartItemsKey = CartKeys.tripPlannerCartItems

export async function invalidateTripChatCartQueries(
  queryClient: QueryClient,
  chatId: number,
  cartIds: Array<string>,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ChatKeys.detail(chatId) }),
    queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatId) }),
    ...cartIds.map(cartId => queryClient.invalidateQueries({ queryKey: tripPlannerCartItemsKey(cartId) })),
  ])
}

interface UseTripPlannerCartItemsArgs {
  multiCartId: string | undefined
  isLoggedIn: boolean
  enabled?: boolean
}

interface UseTripPlannerCartItemsReturn {
  data: TripPlannerCartItemsData | undefined
  cartItemsMap: Map<string, App.MultiCartItem>
  isLoading: boolean
  isFetching: boolean
  isSuccess: boolean
  isError: boolean
  isMissingCart: boolean
  dataUpdatedAt: number
  errorUpdatedAt: number
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

function toRefetchError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Unable to refetch Trip Planner cart items')
}

export function useTripPlannerCartItems({
  multiCartId,
  isLoggedIn,
  enabled = true,
}: UseTripPlannerCartItemsArgs): UseTripPlannerCartItemsReturn {
  const queryEnabled = enabled && isLoggedIn && !!multiCartId
  const {
    data,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    dataUpdatedAt,
    errorUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: tripPlannerCartItemsKey(multiCartId),
    queryFn: () => {
      if (!multiCartId) throw new Error('multiCartId is required for Trip Planner cart items')
      return getMultiCartItems(multiCartId)
    },
    enabled: queryEnabled,
    refetchOnMount: true,
    retry: (failureCount, queryError) => !isMissingTripPlannerCartError(queryError) && failureCount < 1,
  })

  const effectiveData = queryEnabled ? data : undefined
  const cartItemsMap = useMemo(
    () => mapTripPlannerCartItemsToMap(effectiveData, multiCartId),
    [effectiveData, multiCartId],
  )

  const refetchCartItems = useCallback(async() => {
    if (!queryEnabled) return new Map<string, App.MultiCartItem>()
    const result = await refetch()
    if (result.isError) {
      throw toRefetchError(result.error)
    }
    return mapTripPlannerCartItemsToMap(result.data, multiCartId)
  }, [multiCartId, queryEnabled, refetch])

  return {
    data: effectiveData,
    cartItemsMap,
    isLoading: queryEnabled && isLoading,
    isFetching: queryEnabled && isFetching,
    isSuccess: queryEnabled && isSuccess,
    isError: queryEnabled && isError,
    isMissingCart: queryEnabled && isError && isMissingTripPlannerCartError(error),
    dataUpdatedAt: queryEnabled ? dataUpdatedAt : 0,
    errorUpdatedAt: queryEnabled ? errorUpdatedAt : 0,
    refetchCartItems,
  }
}
